/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { Table, ActionMenu, Button, Modal } from '../../design-system';
import { Input, message, Space, Select, Tooltip } from 'antd';
import { PlusOutlined, SearchOutlined, CloseCircleOutlined, HistoryOutlined, EditOutlined } from '@ant-design/icons';
import { BUTTON_PRESETS } from '../../design-system/constants';
import RoleFormModal from './RoleFormModal';
import RateHistoryModal from './RateHistoryModal';
import RoleHistoryModal from './RoleHistoryModal';
import { roleService } from '../../services/roleService';
import { getApiErrorMessage } from '../../utils/errorUtils';
import logger from '../../utils/logger';

const formatSgdRate = (rate) => {
    const value = Number(rate || 0);
    return `SGD ${value.toLocaleString('en-SG', {
        minimumFractionDigits: value % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2
    })}/hour`;
};

const renderResponsibilities = (value) => {
    const text = String(value || '').trim();
    if (!text) {
        return (
            <span style={{ color: '#8a6d00', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 4, padding: '2px 6px' }}>
                Add responsibilities
            </span>
        );
    }
    const preview = text.length > 140 ? `${text.slice(0, 140)}...` : text;
    return (
        <span style={{ whiteSpace: 'pre-line', color: '#4a4a4a' }}>
            {preview}
        </span>
    );
};

const isActiveMember = (member) => member?.is_active !== false;

const splitMembersByStatus = (members = []) => ({
    active: members.filter(isActiveMember),
    inactive: members.filter(member => !isActiveMember(member))
});

const formatMemberList = (members = []) => members.map(member => member.full_name).join(', ');

const memberSearchLabel = (member) => [
    member.full_name,
    member.job_title,
    member.user_role?.name,
    isActiveMember(member) ? 'active' : 'inactive'
].filter(Boolean).join(' ');

const renderStatusBadge = (isActive) => (
    <span
        style={{
            marginLeft: 6,
            padding: '1px 6px',
            borderRadius: 4,
            fontSize: 11,
            border: `1px solid ${isActive ? '#b7eb8f' : '#d9d9d9'}`,
            color: isActive ? '#237804' : '#8c8c8c',
            background: isActive ? '#f6ffed' : '#f5f5f5',
            whiteSpace: 'nowrap'
        }}
    >
        {isActive ? 'Active' : 'Inactive'}
    </span>
);

const renderMemberName = (member) => (
    <span key={member.id} style={{ display: 'inline-flex', alignItems: 'center', marginRight: 6 }}>
        <span>{member.full_name}</span>
        {renderStatusBadge(isActiveMember(member))}
    </span>
);

const renderMemberOption = (member, currentRoleId) => (
    <span>
        <span>{member.full_name}</span>
        {member.job_title ? <span style={{ color: '#8c8c8c' }}> - {member.job_title}</span> : null}
        {member.user_role && Number(member.user_role.id) !== Number(currentRoleId)
            ? <span style={{ color: '#8c8c8c' }}> ({member.user_role.name})</span>
            : null}
        {renderStatusBadge(isActiveMember(member))}
    </span>
);

const renderSetupMetric = ({ label, value, detail, tone = 'neutral' }) => {
    const colors = {
        good: { background: '#f6ffed', border: '#b7eb8f', color: '#237804' },
        warning: { background: '#fffbe6', border: '#ffe58f', color: '#8a6d00' },
        neutral: { background: '#f5f5f5', border: '#d9d9d9', color: '#595959' }
    };
    const palette = colors[tone] || colors.neutral;

    return (
        <div
            style={{
                border: `1px solid ${palette.border}`,
                background: palette.background,
                color: palette.color,
                borderRadius: 8,
                padding: '9px 10px',
                minWidth: 160,
                flex: '1 1 160px'
            }}
        >
            <div style={{ fontSize: 11, textTransform: 'uppercase', fontWeight: 700 }}>{label}</div>
            <div style={{ fontSize: 19, fontWeight: 700, lineHeight: 1.2 }}>{value}</div>
            {detail ? <div style={{ fontSize: 12, marginTop: 2 }}>{detail}</div> : null}
        </div>
    );
};

const RoleList = () => {
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [search, setSearch] = useState('');
    const [departmentFilter, setDepartmentFilter] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState('asc');
    const [modalVisible, setModalVisible] = useState(false);
    const [editingRole, setEditingRole] = useState(null);
    const [historyModalVisible, setHistoryModalVisible] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);
    const [rateHistory, setRateHistory] = useState([]);
    const [roleHistoryModalVisible, setRoleHistoryModalVisible] = useState(false);
    const [roleHistoryRole, setRoleHistoryRole] = useState(null);
    const [roleHistory, setRoleHistory] = useState([]);
    const [roleHistoryLoading, setRoleHistoryLoading] = useState(false);
    const [canManageAll, setCanManageAll] = useState(false);
    const [memberModalVisible, setMemberModalVisible] = useState(false);
    const [memberRole, setMemberRole] = useState(null);
    const [memberOptions, setMemberOptions] = useState([]);
    const [memberIds, setMemberIds] = useState([]);
    const [memberOptionsLoading, setMemberOptionsLoading] = useState(false);
    const [memberSaving, setMemberSaving] = useState(false);
    const [coverage, setCoverage] = useState([]);
    const [coverageLoading, setCoverageLoading] = useState(false);

    useEffect(() => {
        loadDepartments();
    }, []);

    useEffect(() => {
        loadCoverage();
    }, [departmentFilter]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        loadRoles();
    }, [pagination.current, search, departmentFilter, sortBy, sortOrder]);

    const loadDepartments = async () => {
        try {
            const result = await roleService.getManagedDepartments();
            const deptList = result.departments || [];
            setDepartments(deptList);
            setCanManageAll(Boolean(result.can_manage_all));
        } catch (error) {
            logger.error('Error loading departments:', error);
        }
    };

    const loadRoles = async () => {
        setLoading(true);
        try {
            const result = await roleService.getRoles({
                page: pagination.current,
                limit: pagination.pageSize,
                search,
                sortBy,
                sortOrder,
                departmentId: departmentFilter
            });

            setRoles(result.roles || []);
            setCanManageAll(Boolean(result.can_manage_all));
            setPagination({ ...pagination, total: result.total || 0 });
        } catch (error) {
            message.error('Failed to load roles');
        } finally {
            setLoading(false);
        }
    };

    const loadCoverage = async () => {
        setCoverageLoading(true);
        try {
            const result = await roleService.getDepartmentCoverage(departmentFilter);
            setCoverage(result.coverage || []);
        } catch (error) {
            logger.error('Error loading role coverage:', error);
            setCoverage([]);
        } finally {
            setCoverageLoading(false);
        }
    };

    const handleAdd = () => {
        setEditingRole(null);
        setModalVisible(true);
    };

    const handleEdit = (role) => {
        setEditingRole(role);
        setModalVisible(true);
    };

    const handleDelete = async (roleId, roleName, totalUsers) => {
        if (totalUsers > 0) {
            message.error(`Cannot delete role: ${totalUsers} user(s) are assigned to this role`);
            return;
        }

        Modal.confirm({
            title: 'Delete Role',
            content: `Are you sure you want to delete "${roleName}"?`,
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await roleService.deleteRole(roleId);
                    message.success('Role deleted successfully');
                    loadRoles();
                    loadCoverage();
                } catch (error) {
                    if (getApiErrorMessage(error, "").includes('user')) {
                        message.error('Cannot delete role with assigned users');
                    } else {
                        message.error('Failed to delete role');
                    }
                }
            }
        });
    };

    const handleViewRateHistory = async (role) => {
        try {
            const result = await roleService.getRateHistory(role.id);
            setRateHistory(result.rate_history || []);
            setSelectedRole(role);
            setHistoryModalVisible(true);
        } catch (error) {
            message.error('Failed to load rate history');
        }
    };

    const handleRefreshRateHistory = async () => {
        if (selectedRole) {
            try {
                const result = await roleService.getRateHistory(selectedRole.id);
                setRateHistory(result.rate_history || []);
                // Also refresh the roles list to update current_rate
                loadRoles();
            } catch (error) {
                message.error('Failed to refresh rate history');
            }
        }
    };

    const handleHistoryModalClose = () => {
        setHistoryModalVisible(false);
        setSelectedRole(null);
        setRateHistory([]);
    };

    const handleViewRoleHistory = async (role) => {
        setRoleHistoryRole(role);
        setRoleHistory([]);
        setRoleHistoryModalVisible(true);
        setRoleHistoryLoading(true);

        try {
            const result = await roleService.getRoleHistory(role.id);
            setRoleHistory(result.history || []);
        } catch (error) {
            logger.error('Error loading role history:', error);
            message.error('Failed to load history');
        } finally {
            setRoleHistoryLoading(false);
        }
    };

    const handleRoleHistoryModalClose = () => {
        setRoleHistoryModalVisible(false);
        setRoleHistoryRole(null);
        setRoleHistory([]);
    };

    const handleModalClose = (refresh = false) => {
        setModalVisible(false);
        if (refresh) {
            loadRoles();
            loadCoverage();
        }
    };

    const openMemberMapping = async (role) => {
        setMemberRole(role);
        setMemberIds((role.assigned_users || []).map(user => user.id));
        setMemberOptions([]);
        setMemberModalVisible(true);
        setMemberOptionsLoading(true);

        try {
            const result = await roleService.getDepartmentTeamMembers(role.department_id, { includeInactive: true });
            const members = result.team_members || [];
            setMemberOptions(members);
            setMemberIds(
                members
                    .filter(member => Number(member.user_role_id) === Number(role.id))
                    .map(member => member.id)
            );
        } catch (error) {
            logger.error('Error loading role team members:', error);
            message.error('Failed to load team members');
        } finally {
            setMemberOptionsLoading(false);
        }
    };

    const closeMemberMapping = () => {
        setMemberModalVisible(false);
        setMemberRole(null);
        setMemberOptions([]);
        setMemberIds([]);
    };

    const handleSaveMemberMapping = async () => {
        if (!memberRole) return;

        try {
            setMemberSaving(true);
            await roleService.updateRoleTeamMembers(memberRole.id, memberIds);
            message.success('Team members updated successfully');
            closeMemberMapping();
            loadRoles();
            loadCoverage();
        } catch (error) {
            logger.error('Error saving role team members:', error);
            message.error('Failed to update team members');
        } finally {
            setMemberSaving(false);
        }
    };

    const rateCardSummary = useMemo(() => {
        const roleCount = roles.length;
        const activeTotal = coverage.reduce((sum, item) => sum + Number(item.active_total || 0), 0);
        const mappedActiveTotal = coverage.reduce((sum, item) => sum + Number(item.mapped_active_total || 0), 0);
        const activeUnmappedTotal = coverage.reduce((sum, item) => sum + Number(item.unmapped_active_total || 0), 0);
        const inactiveUnmappedTotal = coverage.reduce((sum, item) => sum + Number(item.unmapped_inactive_total || 0), 0);
        const departmentsNeedingMapping = coverage.filter(item => Number(item.unmapped_active_total || 0) > 0);
        const rolesMissingRate = roles.filter(role => Number(role.current_rate || 0) <= 0);
        const rolesMissingResponsibilities = roles.filter(role => !String(role.responsibilities || '').trim());
        const rolesMissingActiveMembers = roles.filter(role => {
            const assignedUsers = role.assigned_users || [];
            return assignedUsers.filter(isActiveMember).length === 0;
        });

        const mappingComplete = coverage.length > 0 && (activeTotal === 0 || activeUnmappedTotal === 0);
        const checks = [
            roleCount > 0,
            roleCount > 0 && rolesMissingRate.length === 0,
            roleCount > 0 && rolesMissingResponsibilities.length === 0,
            mappingComplete
        ];
        const score = Math.round((checks.filter(Boolean).length / checks.length) * 100);

        return {
            roleCount,
            activeTotal,
            mappedActiveTotal,
            activeUnmappedTotal,
            inactiveUnmappedTotal,
            departmentsNeedingMapping,
            rolesMissingRate,
            rolesMissingResponsibilities,
            rolesMissingActiveMembers,
            score
        };
    }, [coverage, roles]);

    const handleReviewMissingDepartment = () => {
        const firstDepartment = rateCardSummary.departmentsNeedingMapping[0];
        if (!firstDepartment) return;

        setDepartmentFilter(firstDepartment.department_id);
        setPagination(current => ({ ...current, current: 1 }));
    };

    const renderRateCardSetup = () => {
        const hasRoles = rateCardSummary.roleCount > 0;
        const nextItems = [
            !hasRoles ? 'Add at least one role for this department' : null,
            rateCardSummary.rolesMissingRate.length > 0 ? `${rateCardSummary.rolesMissingRate.length} role${rateCardSummary.rolesMissingRate.length === 1 ? '' : 's'} missing public rate` : null,
            rateCardSummary.rolesMissingResponsibilities.length > 0 ? `${rateCardSummary.rolesMissingResponsibilities.length} role${rateCardSummary.rolesMissingResponsibilities.length === 1 ? '' : 's'} missing responsibilities` : null,
            rateCardSummary.activeUnmappedTotal > 0 ? `${rateCardSummary.activeUnmappedTotal} active employee${rateCardSummary.activeUnmappedTotal === 1 ? '' : 's'} not mapped` : null,
            rateCardSummary.rolesMissingActiveMembers.length > 0 ? `${rateCardSummary.rolesMissingActiveMembers.length} role${rateCardSummary.rolesMissingActiveMembers.length === 1 ? '' : 's'} without active members` : null
        ].filter(Boolean).slice(0, 4);

        return (
            <div
                style={{
                    marginBottom: 16,
                    padding: '14px 16px',
                    border: '1px solid #e8e8e8',
                    borderRadius: 8,
                    background: '#fff'
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 280, flex: 1 }}>
                        <div style={{ fontWeight: 700 }}>Rate card setup</div>
                        <div style={{ color: '#8c8c8c', fontSize: 13, marginTop: 3 }}>
                            Complete roles, rates, responsibilities, and team mapping so project estimates can reuse this card.
                        </div>
                        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ flex: 1, height: 7, background: '#f0f0f0', borderRadius: 999, overflow: 'hidden' }}>
                                <div
                                    style={{
                                        width: `${rateCardSummary.score}%`,
                                        height: '100%',
                                        background: rateCardSummary.score === 100 ? '#52c41a' : '#f6c343'
                                    }}
                                />
                            </div>
                            <strong style={{ minWidth: 44, textAlign: 'right' }}>{rateCardSummary.score}%</strong>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 8, flex: '1 1 520px', flexWrap: 'wrap' }}>
                        {renderSetupMetric({
                            label: 'Active team mapped',
                            value: `${rateCardSummary.mappedActiveTotal}/${rateCardSummary.activeTotal}`,
                            detail: rateCardSummary.activeUnmappedTotal > 0 ? `${rateCardSummary.activeUnmappedTotal} active missing` : 'All active mapped',
                            tone: rateCardSummary.activeUnmappedTotal > 0 ? 'warning' : 'good'
                        })}
                        {renderSetupMetric({
                            label: 'Roles shown',
                            value: rateCardSummary.roleCount,
                            detail: rateCardSummary.rolesMissingRate.length > 0 ? `${rateCardSummary.rolesMissingRate.length} missing rate` : 'Rates complete',
                            tone: rateCardSummary.rolesMissingRate.length > 0 || !hasRoles ? 'warning' : 'good'
                        })}
                        {renderSetupMetric({
                            label: 'Responsibilities',
                            value: `${Math.max(rateCardSummary.roleCount - rateCardSummary.rolesMissingResponsibilities.length, 0)}/${rateCardSummary.roleCount}`,
                            detail: rateCardSummary.rolesMissingResponsibilities.length > 0 ? 'Add role notes' : 'Complete',
                            tone: rateCardSummary.rolesMissingResponsibilities.length > 0 || !hasRoles ? 'warning' : 'good'
                        })}
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginTop: 12, alignItems: 'center' }}>
                    <div style={{ color: nextItems.length ? '#595959' : '#237804', fontSize: 13 }}>
                        {nextItems.length ? `Next: ${nextItems.join(' · ')}` : 'This rate card is ready for the selected view.'}
                    </div>
                    <Space size="small">
                        {rateCardSummary.departmentsNeedingMapping.length > 0 ? (
                            <Button
                                variant="secondary"
                                size="small"
                                onClick={handleReviewMissingDepartment}
                            >
                                REVIEW MISSING
                            </Button>
                        ) : null}
                        <Button
                            className="btn-white-add"
                            icon={<PlusOutlined />}
                            onClick={handleAdd}
                            variant="secondary"
                            size="small"
                        >
                            ADD ROLE
                        </Button>
                    </Space>
                </div>
            </div>
        );
    };

    const renderTeamMembers = (_, record) => {
        const assignedUsers = record.assigned_users || [];
        const { active, inactive } = splitMembersByStatus(assignedUsers);
        const visibleMembers = assignedUsers.slice(0, 3);
        const extraCount = Math.max(assignedUsers.length - visibleMembers.length, 0);
        const tooltipTitle = assignedUsers.length ? (
            <div>
                {active.length > 0 ? <div><strong>Active:</strong> {formatMemberList(active)}</div> : null}
                {inactive.length > 0 ? <div><strong>Inactive:</strong> {formatMemberList(inactive)}</div> : null}
            </div>
        ) : 'No team members mapped';

        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <Tooltip title={tooltipTitle}>
                    <div style={{ minWidth: 0, textAlign: 'left', color: assignedUsers.length ? '#262626' : '#8c8c8c' }}>
                        {assignedUsers.length ? (
                            <>
                                <div style={{ whiteSpace: 'normal' }}>
                                    {visibleMembers.map(renderMemberName)}
                                    {extraCount > 0 ? ` +${extraCount} more` : ''}
                                </div>
                                <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                    {active.length} active{inactive.length > 0 ? `, ${inactive.length} inactive` : ''} mapped
                                </div>
                            </>
                        ) : (
                            <span style={{ color: '#8a6d00', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 4, padding: '2px 6px' }}>
                                Map members
                            </span>
                        )}
                    </div>
                </Tooltip>
                <Tooltip title="Edit team members">
                    <Button
                        variant="secondary"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => openMemberMapping(record)}
                        aria-label={`Edit team members for ${record.name}`}
                    />
                </Tooltip>
            </div>
        );
    };

    const renderPublicRate = (_, record) => {
        const hasRate = Number(record.current_rate || 0) > 0;

        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
                {hasRate ? (
                    <span>{formatSgdRate(record.current_rate)}</span>
                ) : (
                    <span style={{ color: '#8a6d00', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 4, padding: '2px 6px' }}>
                        Set public rate
                    </span>
                )}
                <Button
                    variant="secondary"
                    size="small"
                    icon={<HistoryOutlined />}
                    uppercase={false}
                    onClick={() => handleViewRoleHistory(record)}
                    aria-label={`View history for ${record.name}`}
                    style={{ padding: '0 10px' }}
                >
                    View history
                </Button>
            </div>
        );
    };

    const renderCoverageNames = (members = []) => {
        if (members.length === 0) return <span style={{ color: '#8c8c8c' }}>None</span>;

        return members.map(member => (
            <span key={member.id} style={{ display: 'inline-flex', alignItems: 'center', marginRight: 10, marginBottom: 4 }}>
                <span>{member.full_name}</span>
                {member.job_title ? <span style={{ color: '#8c8c8c', marginLeft: 4 }}>({member.job_title})</span> : null}
            </span>
        ));
    };

    const renderCoveragePanel = () => {
        if (coverageLoading) {
            return (
                <div style={{ marginBottom: 16, padding: '12px 16px', border: '1px solid #e8e8e8', borderRadius: 8 }}>
                    Checking unmapped employees...
                </div>
            );
        }

        if (!coverage.length) return null;

        const departmentsWithMissing = coverage.filter(item => Number(item.unmapped_total || 0) > 0);

        return (
            <div style={{ marginBottom: 16, padding: '14px 16px', border: '1px solid #e8e8e8', borderRadius: 8, background: '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
                    <div>
                        <div style={{ fontWeight: 600 }}>Employees not mapped to a billable role</div>
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                            {departmentsWithMissing.length > 0
                                ? `${departmentsWithMissing.length} department${departmentsWithMissing.length === 1 ? '' : 's'} need mapping`
                                : 'All employees are mapped for the selected department view'}
                        </div>
                    </div>
                </div>

                {departmentsWithMissing.length > 0 ? (
                    <div style={{ display: 'grid', gap: 10, maxHeight: 220, overflowY: 'auto' }}>
                        {departmentsWithMissing.map(item => (
                            <div
                                key={item.department_id}
                                style={{ borderTop: '1px solid #f0f0f0', paddingTop: 10 }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                                    <strong>{item.department_name}</strong>
                                    <span style={{ color: '#8c8c8c', fontSize: 12 }}>
                                        {item.unmapped_active_total} active, {item.unmapped_inactive_total} inactive not mapped
                                    </span>
                                </div>
                                <div style={{ marginTop: 6, fontSize: 13 }}>
                                    <div>
                                        <span style={{ fontWeight: 600 }}>Active:</span>{' '}
                                        {renderCoverageNames(item.active_unmapped_employees || [])}
                                    </div>
                                    {Number(item.unmapped_inactive_total || 0) > 0 ? (
                                        <div style={{ marginTop: 4 }}>
                                            <span style={{ fontWeight: 600 }}>Inactive:</span>{' '}
                                            {renderCoverageNames(item.inactive_unmapped_employees || [])}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : null}
            </div>
        );
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: '20%',
            sorter: true,
            sortOrder: sortBy === 'name' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null
        },
        {
            title: 'Responsibilities',
            dataIndex: 'responsibilities',
            key: 'responsibilities',
            width: '30%',
            render: renderResponsibilities
        },
        {
            title: 'Department',
            dataIndex: 'department_name',
            key: 'department_name',
            width: '20%'
        },
        {
            title: 'Team Members',
            dataIndex: 'assigned_users',
            key: 'total_users',
            width: '20%',
            render: renderTeamMembers
        },
        {
            title: 'Public Rate',
            dataIndex: 'current_rate',
            key: 'current_rate',
            width: '15%',
            render: renderPublicRate
        },
        {
            title: '',
            key: 'actions',
            align: 'center',
            width: 80,
            render: (_, record) => (
                <ActionMenu
                    items={[
                        {
                            key: 'history',
                            label: 'Manage Rate History',
                            icon: <HistoryOutlined style={{ color: '#4A4A4A' }} />
                        },
                        'edit',
                        'delete'
                    ]}
                    record={record}
                    onAction={(key, record) => {
                        if (key === 'history') {
                            handleViewRateHistory(record);
                        } else if (key === 'edit') {
                            handleEdit(record);
                        } else if (key === 'delete') {
                            handleDelete(record.id, record.name, record.total_users);
                        }
                    }}
                    disabledKeys={record.total_users > 0 ? ['delete'] : []}
                />
            )
        }
    ];

    const handleTableChange = (pagination, filters, sorter) => {
        setPagination(pagination);
        if (sorter.field) {
            setSortBy(sorter.field);
            setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
        }
    };

    return (
        <div>
            {/* Header with Add Button, Department Filter, and Search */}
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <div>
                    <div style={{ fontWeight: 700 }}>Billable Rate Card</div>
                    <div style={{ color: '#8c8c8c', fontSize: 13 }}>
                        Define the roles, rates, responsibilities, and people used for project cost estimates.
                    </div>
                </div>
                <Space size="small">
                    <Button
                        className="btn-white-add"
                        icon={<PlusOutlined />}
                        onClick={handleAdd}
                        variant="secondary"
                        size="small"
                    >
                        ADD ROLE
                    </Button>

                    {canManageAll && (
                        <Select
                            placeholder="All Departments"
                            style={{ minWidth: 200 }}
                            allowClear
                            value={departmentFilter}
                            onChange={(value) => {
                                setDepartmentFilter(value);
                                setPagination(current => ({ ...current, current: 1 }));
                            }}
                            showSearch
                            filterOption={(input, option) =>
                                option.children.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {departments.map(dept => (
                                <Select.Option key={dept.id} value={dept.id}>
                                    {dept.name}
                                </Select.Option>
                            ))}
                        </Select>
                    )}

                    <Input
                        placeholder="Search"
                        style={{ minWidth: 250 }}
                        prefix={<SearchOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                        suffix={
                            search ? (
                                <CloseCircleOutlined
                                    style={{
                                        color: "rgba(0,0,0,.25)",
                                        cursor: "pointer",
                                    }}
                                    onClick={() => setSearch("")}
                                />
                            ) : null
                        }
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </Space>
            </div>

            {renderRateCardSetup()}

            {renderCoveragePanel()}

            {/* Table */}
            <Table
                columns={columns}
                dataSource={roles}
                loading={loading}
                rowKey="id"
                pagination={pagination}
                onChange={handleTableChange}
                variant="bordered"
            />

            {/* Form Modal */}
            {modalVisible && (
                <RoleFormModal
                    visible={modalVisible}
                    role={editingRole}
                    onClose={handleModalClose}
                />
            )}

            {/* Rate History Modal */}
            {historyModalVisible && (
                <RateHistoryModal
                    visible={historyModalVisible}
                    role={selectedRole}
                    rateHistory={rateHistory}
                    onClose={handleHistoryModalClose}
                    onRefresh={handleRefreshRateHistory}
                />
            )}

            {roleHistoryModalVisible && (
                <RoleHistoryModal
                    visible={roleHistoryModalVisible}
                    role={roleHistoryRole}
                    history={roleHistory}
                    loading={roleHistoryLoading}
                    onClose={handleRoleHistoryModalClose}
                />
            )}

            {memberModalVisible && (
                <Modal visible={memberModalVisible} onClose={closeMemberMapping} width="small">
                    <Modal.Header
                        title="Edit team members"
                        subtitle={memberRole ? `${memberRole.department_name} - ${memberRole.name}` : ''}
                    />
                    <Modal.Body padding="default">
                        <Select
                            mode="multiple"
                            placeholder="Select team members"
                            value={memberIds}
                            onChange={setMemberIds}
                            loading={memberOptionsLoading}
                            disabled={memberOptionsLoading}
                            showSearch
                            optionFilterProp="label"
                            style={{ width: '100%' }}
                        >
                            {memberOptions.map(member => (
                                <Select.Option key={member.id} value={member.id} label={memberSearchLabel(member)}>
                                    {renderMemberOption(member, memberRole?.id)}
                                </Select.Option>
                            ))}
                        </Select>
                    </Modal.Body>
                    <Modal.Footer buttons={[
                        {
                            ...BUTTON_PRESETS.CANCEL,
                            onClick: closeMemberMapping
                        },
                        {
                            ...BUTTON_PRESETS.SAVE,
                            label: 'Save',
                            loading: memberSaving,
                            onClick: handleSaveMemberMapping
                        }
                    ]} />
                </Modal>
            )}
        </div>
    );
};

export default RoleList;
